import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, InputBase,
  Paper, Chip, Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import PaymentsIcon from '@mui/icons-material/Payments';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BarChartIcon from '@mui/icons-material/BarChart';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';

// ─── Color tokens ──────────────────────────────────────────────────────────
const HOT_PINK  = '#FF1E8C';
const ORANGE    = '#FF6B35';
const GREEN     = '#00D97E';
const PURPLE    = '#9B5DE5';
const YELLOW    = '#FFD600';
const TEAL      = '#00BFA5';

// ─── Category data ─────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 32 }} />,
    title: 'Getting Started',
    subtitle: 'New to InfHire? Start here',
    articles: 6,
    color: HOT_PINK,
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 32 }} />,
    title: 'For Brands',
    subtitle: 'Run and manage influencer campaigns',
    articles: 11,
    color: ORANGE,
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
    title: 'For Creators',
    subtitle: 'Find campaigns and get paid',
    articles: 9,
    color: PURPLE,
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 32 }} />,
    title: 'Discover Creators',
    subtitle: 'Find the right influencers for your brand',
    articles: 8,
    color: TEAL,
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 32 }} />,
    title: 'Campaigns & Analytics',
    subtitle: 'Create, manage and track performance',
    articles: 10,
    color: GREEN,
  },
  {
    icon: <PaymentsIcon sx={{ fontSize: 32 }} />,
    title: 'Payments',
    subtitle: 'Invoices, payouts and billing',
    articles: 7,
    color: YELLOW,
  },
  {
    icon: <ManageAccountsIcon sx={{ fontSize: 32 }} />,
    title: 'Account & Settings',
    subtitle: 'Profile, preferences and security',
    articles: 5,
    color: HOT_PINK,
  },
  {
    icon: <HelpOutlineIcon sx={{ fontSize: 32 }} />,
    title: 'Platform Guide',
    subtitle: 'Features, integrations and tips',
    articles: 12,
    color: ORANGE,
  },
];

// ─── Popular articles ──────────────────────────────────────────────────────
const POPULAR = [
  {
    icon: <RocketLaunchIcon />,
    title: 'How to create your first campaign',
    desc: 'Step-by-step guide to launching your first influencer campaign on InfHire.',
    color: HOT_PINK,
  },
  {
    icon: <PeopleIcon />,
    title: 'How to discover and invite influencers',
    desc: 'Use the Discover tab to find creators by niche, platform, and engagement rate.',
    color: TEAL,
  },
  {
    icon: <AutoAwesomeIcon />,
    title: 'How to apply to campaigns as a creator',
    desc: 'Browse open campaigns, submit your pitch, and manage your applications.',
    color: PURPLE,
  },
  {
    icon: <CampaignIcon />,
    title: 'Setting up your brand profile',
    desc: 'Complete your brand profile to attract the right creators to your campaigns.',
    color: ORANGE,
  },
  {
    icon: <BarChartIcon />,
    title: 'Understanding campaign analytics',
    desc: 'Learn how to read reach, engagement, and ROI metrics on your dashboard.',
    color: GREEN,
  },
  {
    icon: <PaymentsIcon />,
    title: 'How payouts work for creators',
    desc: 'Learn how and when you get paid after a campaign post is approved.',
    color: YELLOW,
  },
];

// ─── Main Component ────────────────────────────────────────────────────────
const HelpCenter = () => {
  const theme = useTheme();
  const dark  = theme.palette.mode === 'dark';
  const [query, setQuery] = useState('');

  const surface = dark ? '#1a1a1a' : '#ffffff';
  const border  = dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e6e6e6';
  const text    = dark ? '#f1f5f9' : '#1a1a1a';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: dark ? '#0a0a0a' : '#f9f9f9' }}>

      {/* ── Hero ── */}
      <Box
        sx={{
          bgcolor: dark ? '#1a0a1a' : '#FFEEF7',
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 10 },
          borderBottom: border,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip
            label="Help Center"
            sx={{
              mb: 3,
              bgcolor: `${HOT_PINK}18`,
              color: HOT_PINK,
              fontWeight: 700,
              border: `1px solid ${HOT_PINK}30`,
              fontSize: '0.85rem',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: text,
              fontSize: { xs: '2rem', md: '3rem' },
              letterSpacing: '-1px',
              mb: 1.5,
            }}
          >
            How can we help you?
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'text.secondary', fontWeight: 400, mb: 5 }}
          >
            Search our knowledge base or browse categories below.
          </Typography>

          {/* Search bar */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1.5,
              borderRadius: '50px',
              border: `2px solid ${query ? HOT_PINK : (dark ? 'rgba(255,255,255,0.12)' : '#e0e0e0')}`,
              bgcolor: surface,
              maxWidth: 560,
              mx: 'auto',
              transition: 'border-color 0.2s',
              boxShadow: query ? `0 4px 20px ${HOT_PINK}20` : 'none',
            }}
          >
            <SearchIcon sx={{ color: query ? HOT_PINK : 'text.disabled', flexShrink: 0 }} />
            <InputBase
              fullWidth
              placeholder="Search for articles..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              sx={{ fontSize: '1rem', color: text }}
            />
          </Paper>
        </Container>
      </Box>

      {/* ── Category Cards ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: text, mb: 4, letterSpacing: '-0.5px' }}
        >
          Browse by category
        </Typography>

        <Grid container spacing={3}>
          {CATEGORIES.map((cat) => (
            <Grid item xs={12} sm={6} md={4} key={cat.title}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: surface,
                  border,
                  borderRadius: '20px',
                  p: 3,
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${cat.color}20`,
                    borderColor: `${cat.color}50`,
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    bgcolor: `${cat.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: cat.color,
                  }}
                >
                  {cat.icon}
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 800, color: text, mb: 0.5 }}>
                  {cat.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {cat.subtitle}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: cat.color,
                    fontWeight: 700,
                    bgcolor: `${cat.color}12`,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '50px',
                  }}
                >
                  {cat.articles} articles
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Popular Articles ── */}
      <Box sx={{ bgcolor: dark ? '#111111' : '#ffffff', py: { xs: 6, md: 8 }, borderTop: border, borderBottom: border }}>
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: text, mb: 4, letterSpacing: '-0.5px' }}
          >
            Popular articles
          </Typography>

          <Grid container spacing={3}>
            {POPULAR.map((art) => (
              <Grid item xs={12} sm={6} key={art.title}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: surface,
                    border,
                    borderRadius: '16px',
                    p: 3,
                    display: 'flex',
                    gap: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${art.color}18`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: `${art.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: art.color,
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {art.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: text,
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {art.title}
                      <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {art.desc}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Still need help? ── */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: `${HOT_PINK}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <EmailIcon sx={{ fontSize: 30, color: HOT_PINK }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: text, mb: 1 }}>
            Still need help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Can't find what you're looking for? Our support team is ready to help.
          </Typography>
          <Link
            href="mailto:support@infhire.com"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: HOT_PINK,
              color: '#fff',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.5,
              borderRadius: '50px',
              textDecoration: 'none',
              boxShadow: `0 8px 24px ${HOT_PINK}40`,
              transition: 'background 0.2s, box-shadow 0.2s',
              '&:hover': {
                bgcolor: '#e0177d',
                boxShadow: `0 12px 32px ${HOT_PINK}50`,
              },
            }}
          >
            Contact Support
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Link>
        </Container>
      </Box>

    </Box>
  );
};

export default HelpCenter;
